import _ from 'lodash'
import fetch, { FetchOptions, GraphVariantOptions } from '@adc/fetch'
import configment from '@adc/configment'
import { constraints } from '@adc/constants'
import format from './graphFormatter'
import {
  CollectionResult,
  CoordinatesInput,
  MarketingAttributeClass,
  MarketingAttributeType,
  ContentMarketingAttribute,
} from '@adc/sdk/types'

class UnknownSeekTypeError extends Error {}
class SeekQueryEmptyFieldsError extends Error {}
class SeekBoundsViolationError extends Error {}

// NOTE: This is the same hash function that's used and tested in @adc/core-utils but it's a private detail. Need more
// investigation into whether can export as a shared helper.
function hash(str: string): string {
  var hash = 0

  for (var i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash = hash & hash
  }

  return hash.toString(16).replace('-', '')
}

const LIB_OPERATION_IDENTIFIER = 'adcSeek'

const calculateDefaultOperationName = (
  seekCollection: string,
  formattedQuery: string
) => {
  let appName = ''

  // NOTE: By default we include the appName so we have a way to know where the
  // anonymous seek query is coming from so we can know where to fix it.
  try {
    appName = configment.get('APP_NAME')
  } catch {
    // swallow
  }

  return [
    _.camelCase(appName),
    LIB_OPERATION_IDENTIFIER,
    _.camelCase(seekCollection),
    hash(formattedQuery),
  ]
    .filter(Boolean)
    .join('_')
}

type StringOrNumber = string | number
type SortCondition = Record<string, any>
type ValueOrArrayOrRecord<T> = T | Array<T> | Record<string, T>
type ValueOrRecord<T> =
  | ValueOrArrayOrRecord<T>
  | Array<ValueOrArrayOrRecord<T>>
  | Record<string, ValueOrArrayOrRecord<T>>
type FragmentDefinition = {
  on?: string
  fragment: Array<string> | string
}
type FieldsConfig = {
  fragmentDefinitions: Record<string, FragmentDefinition>
}
interface RecordOfValueOrRecord<T> extends Record<string, ValueOrRecord<T>> {}

interface Query {
  toObject(): Record<string, any>
}

interface SpringPaginationWrapper<T> {
  content: T[]
  total_count: number
  total_elements: number
  total_pages: number
  current_page: number
  size: number
}

type SeekSortScript = {
  name: string
  type: string
  arguments?: Record<string, any>
}

type SeekResponse = SpringPaginationWrapper<any>

type SeekIterationOptions = {
  batchSize: number
  pageSize: number
}

type SeekAggregation = {
  alias?: string
  fields: string[]
}

const GENERIC_ITERATION_COMPLETION = {
  value: undefined,
  done: true,
}

enum SeekTypes {
  EVENTS = 'EVENTS',
  EVENT_TEMPLATES = 'EVENT_TEMPLATES',
  GLOBAL_MEDIA = 'GLOBAL_MEDIA',
  LISTINGS = 'LISTINGS',
  ACTIVE_LISTINGS = 'ACTIVE_LISTINGS',
  MARKETING_PACKAGES = 'MARKETING_PACKAGES',
  MEDIA_PACKAGES = 'MEDIA_PACKAGES',
  MLH_SYNC_LOGS = 'MLH_SYNC_LOGS',
  MORPH_LOGS = 'MORPH_LOGS',
  PROPERTIES = 'PROPERTIES',
  VENUES = 'VENUES',
  VENUE_TEMPLATES = 'VENUE_TEMPLATES',
  AUCTIONEER_DASHBOARD_PROJECTIONS = 'AUCTIONEER_DASHBOARD_PROJECTIONS',
  ATTORNEY_DASHBOARD_PROJECTIONS = 'ATTORNEY_DASHBOARD_PROJECTIONS',
  INVITATIONS = 'INVITATIONS',
}

interface BatchExecuteArgs {
  onData: (items: unknown[]) => void
  includeFieldsInDsl: boolean
  maxBatches?: number
}

// Some indices are scoped versions of other types. They resolve to the same types but have some other conditions
// that make them more efficient in certain situations. We store that direct mapping here so when a client wants to
// use index refernce we can resolve the correct type.
const seekTypesQueryLookup = new Map<SeekTypes, string>([
  [SeekTypes.ACTIVE_LISTINGS, 'Listing'],
])

type MarketingAttributes = {
  types: MarketingAttributeType[]
  classes: MarketingAttributeClass[]
  resolveTags: boolean
  fields: [keyof ContentMarketingAttribute]
}

const defaultMarketingAttributes: MarketingAttributes = {
  types: [],
  classes: [],
  resolveTags: false,
  fields: ['label'],
}

type RenderedMarketingAttributes = {
  types: string
  classes: string
  resolveTags: string
  fields: string
}

class SeekRequest {
  static for(kind: SeekTypes): SeekRequest {
    if (!Object.values(SeekTypes).includes(kind)) {
      throw new UnknownSeekTypeError()
    }

    return new SeekRequest(kind)
  }

  private _kind: SeekTypes
  private _offset: number
  private _size: number
  private _sort: SortCondition[]
  private _query: Query
  private _operationName: string | null
  private _fields: string[]
  private _fieldsConfig: Record<string, any>
  private _implicitAggregations: SeekAggregation[]
  private _marketingAttributes?: MarketingAttributes

  constructor(kind: SeekTypes) {
    this._kind = kind
    this._offset = 0
    this._size = 20
    this._sort = []
    this._query = new AllQuery()
    this._operationName = null
    this._fields = []
    this._implicitAggregations = []
    this._fieldsConfig = {
      fragmentDefinitions: {},
    }
  }

  size(s: number) {
    if (s > constraints.BACKEND_MAX_PAGE_SIZE) {
      throw new SeekBoundsViolationError()
    }

    this._size = s
    return this
  }

  offset(o: number) {
    this._offset = o
    return this
  }

  fields(f: string[], c?: FieldsConfig) {
    this._fields = f

    if (c) {
      this._fieldsConfig = c
    }

    return this
  }

  operationName(name: string) {
    this._operationName = name
    return this
  }

  implicitAggregations(aggregations: SeekAggregation[]) {
    this._implicitAggregations = aggregations
    return this
  }

  query(builder: (q: SeekQuery) => void) {
    this._query = new SeekQuery()
    builder(this._query as SeekQuery)
    return this
  }

  // It's common for us to know the elasticsearch dsl in advance, and to pass around the dsl
  // the search service will use. To support that, this method is here to avoid any builder pattern nicities planned for
  // the `query` method. This just assumes the DSL to be whatever is given, which is also nice if the dsl is calculated
  // elsewhere, like in another library. That's a common pattern Morpheus adopted a while back, so it helps adoption for
  // this library if it can drop-in and work without requiring major modifications to existing code.
  dsl(dsl: RecordOfValueOrRecord<StringOrNumber>) {
    this._query = {
      toObject: () => dsl,
    }

    return this
  }

  marketingAttributes(
    options: MarketingAttributes = defaultMarketingAttributes
  ) {
    this._marketingAttributes = {
      ...defaultMarketingAttributes,
      ..._.cloneDeep(options),
    }
    return this
  }

  sortByField(field: string, order: string = 'DESC') {
    this._sort.push({
      field,
      order: order.toUpperCase(),
    })

    return this
  }

  sortByScript(script: SeekSortScript, order: string = 'DESC') {
    this._sort.push({
      script,
      order: order.toUpperCase(),
    })

    return this
  }

  sortByGeo(
    field: string,
    center: Required<CoordinatesInput>,
    order: string = 'ASC'
  ) {
    this._sort.push({
      geo_distance: {
        field,
        center,
      },
      order: order.toUpperCase(),
    })

    return this
  }

  toObject(
    includeFieldsInDsl = true,
    dangerousSizeOverride?: number
  ): Record<string, any> {
    return {
      query: this._query.toObject(),
      size: dangerousSizeOverride ?? this._size,
      offset: this._offset,
      sort: this._sort,
      ...(includeFieldsInDsl ? { fields: this._applicableFields } : {}),
    }
  }

  execute(
    opts: FetchOptions = {},
    { includeFieldsInDsl = false, dangerousSizeOverride = undefined } = {}
  ) {
    return fetch
      .forAuctionGraph<'seek', CollectionResult>(
        this.toParameterizedQuery(includeFieldsInDsl, dangerousSizeOverride), // Pass the size parameter to the query
        opts
      )
      .then((res: Record<string, any>) => res.body)
  }

  /**
   * batchSize here is a maximum value. Batches will never be greater than the specifice size, but are allowed to
   * contain fewer items if the the final data iteration doesn't have enough items to completely fillll the batch length
   */
  iterator(
    opts?: SeekIterationOptions,
    { includeFieldsInDsl = false } = {}
  ): AsyncIterableIterator<any> {
    const { batchSize = 1, pageSize = 60 } = opts || {}

    if (pageSize % batchSize !== 0) {
      throw new Error(
        'Invalid batchSize and pageSize combination. Must be a divisor.'
      )
    }

    let currentPage = 1
    let currentPageIndex = 0
    let currentBatchIndex = 0
    let expectedIterationPages = 1

    // @ts-ignore
    const fetchNextPage: () => Promise<SeekResponse> = () => {
      this._size = pageSize
      this._offset = (currentPage - 1) * pageSize

      return this.execute({}, { includeFieldsInDsl }).then(
        (res: Record<string, any>) => {
          // First request, let's see what the index tells us
          // TODO: Can I rely on total_pages here?
          if (currentPage === 1) {
            // @ts-ignore
            expectedIterationPages = res.data.seek.total_pages
          }

          // TODO: What do we do if the API throws and error in the middle of an iteration, possible due to a 5xx error in
          // the service, or a partial data issue in the graph, where data at specific offsets come back malformed.
          // @ts-ignore
          return res.data.seek
        }
      )
    }

    // We need an initial request we fire off right away
    let fetcherPromise = fetchNextPage()
    // @ts-ignore
    const iterateForNextPage = (previousData: SeekResponse) => {
      if (currentPageIndex < previousData.content.length) {
        // Single-item iteration is being handled like a special case here, so you get a value for 1, an array of values
        // for n where n > 1. Slices here to the underlying structure would be lovely.
        //
        // Tricky thing here is managing batches that flow between pages. To avoid that, try and keep the batch size
        // and exact divisor of the page size.
        //
        // NOTE: I coded this but it seemed more trouble than it was worth. Not only do we have to store the partial
        // collection, we also need to store the offset from which we slice the items to fulfil the batch
        //
        if (batchSize > 1) {
          const value = []

          let localBatchSize = 0

          while (
            localBatchSize < batchSize &&
            currentBatchIndex < previousData.content.length
          ) {
            value.push(previousData.content[currentBatchIndex])
            localBatchSize++, currentBatchIndex++, currentPageIndex++
          }

          return { value, done: false }
        } else {
          const value = previousData.content[currentPageIndex]

          currentPageIndex += 1

          return { value, done: false }
        }
      }

      // Exhausted current buffered data. Fetch more from remote source.
      currentPageIndex = 0
      currentBatchIndex = 0
      currentPage += 1

      if (currentPage <= expectedIterationPages) {
        fetcherPromise = fetchNextPage()

        return fetcherPromise.then(iterateForNextPage)
      }

      return GENERIC_ITERATION_COMPLETION
    }

    const resolveRemainingPromises = () =>
      fetcherPromise.then(iterateForNextPage)

    const iteratorMethods = {
      [Symbol.asyncIterator]: () => iteratorMethods,
      next: resolveRemainingPromises,
      return: () => Promise.resolve(GENERIC_ITERATION_COMPLETION),
    }

    return iteratorMethods
  }

  async dangerouslyExecuteAll({ includeFieldsInDsl = false } = {}): Promise<
    Array<any>
  > {
    const total = []
    const asyncIterator = this.iterator(
      {
        batchSize: constraints.BACKEND_MAX_PAGE_SIZE,
        pageSize: constraints.BACKEND_MAX_PAGE_SIZE,
      },
      { includeFieldsInDsl }
    )

    for await (let items of asyncIterator) {
      total.push(...items)
    }

    return Promise.resolve(total)
  }

  async batchExecute({
    onData = _.noop,
    includeFieldsInDsl = false,
    maxBatches = undefined,
  }: Partial<BatchExecuteArgs> = {}): Promise<void> {
    const asyncIterator = this.iterator(
      {
        batchSize: constraints.BACKEND_MAX_PAGE_SIZE,
        pageSize: constraints.BACKEND_MAX_PAGE_SIZE,
      },
      { includeFieldsInDsl }
    )
    let batchCount = 0
    let items
    for await (items of asyncIterator) {
      if (typeof maxBatches !== 'undefined') {
        onData(items)
        if (batchCount >= maxBatches - 1) break
        else batchCount++
      } else {
        onData(items) // always onData if there's no maxBatches since at this point the data is already fetched
      }
    }
    return Promise.resolve()
  }

  toQuery(includeFieldsInDsl = true, dangerousSizeOverride?: number): string {
    return this.buildQuery(
      false /* parameterized */,
      includeFieldsInDsl,
      dangerousSizeOverride
    ) as string
  }

  toParameterizedQuery(
    includeFieldsInDsl = true,
    dangerousSizeOverride?: number
  ): GraphVariantOptions {
    return this.buildQuery(
      true /* parameterized */,
      includeFieldsInDsl,
      dangerousSizeOverride
    ) as GraphVariantOptions
  }

  private buildQuery(
    parameterized: boolean,
    includeFieldsInDsl = true,
    dangerousSizeOverride?: number
  ): string | GraphVariantOptions {
    if (this._fields.length === 0) {
      throw new SeekQueryEmptyFieldsError()
    }

    const stringifiedDsl = JSON.stringify(
      this.toObject(includeFieldsInDsl, dangerousSizeOverride)
    )
    const escapedDsl = JSON.stringify(stringifiedDsl)

    const typeName =
      seekTypesQueryLookup.get(this._kind) ||
      this._kind.slice(0, 1) +
        this._kind
          .slice(1)
          .toLowerCase()
          .replace(/_([a-z]{1})/g, l => l[1].toUpperCase())
          .slice(0, -1)

    const formatted = format(this._fields, this._fieldsConfig, 8)
    const operationName =
      this._operationName ||
      calculateDefaultOperationName(this._kind, formatted.fields)

    let implicitAggregationsSnippet: string[] = []

    if (this._implicitAggregations) {
      implicitAggregationsSnippet = this._implicitAggregations.map(agg => {
        let resolver = `aggregation(fields: [${agg.fields.map(f => `"${f}"`)}])`

        if (agg.alias) {
          resolver = `${agg.alias}: ${resolver}`
        }

        return resolver
      })
    }

    type CreateQueryStringParams = {
      operationName: string
      type: string
      dsl: string
      marketingAttributes?: RenderedMarketingAttributes
    }

    const createQueryString = ({
      operationName,
      type,
      dsl,
      marketingAttributes,
    }: CreateQueryStringParams) => {
      let marketingAttributesString = ''
      if (marketingAttributes) {
        marketingAttributesString = `
          marketing_attributes(
            attributeTypes: ${marketingAttributes.types},
            attributeClasses: ${marketingAttributes.classes},
            resolveTags: ${marketingAttributes.resolveTags}
          ) {
            ... on ContentMarketingAttribute {
              ${marketingAttributes.fields}
            }
          }
        `.trim()
      }
      return `
      ${formatted.fragments}
      query ${operationName} {
        seek(type: ${type}, dsl: ${dsl}) {
          current_page
          size
          total_count
          total_elements
          total_pages
          content {
            ... on ${typeName} {
              ${formatted.fields}
              ${marketingAttributesString}
            }
          }
          ${implicitAggregationsSnippet
            .map(entry => ' '.repeat(4) + entry)
            .join('\n')}
        }
      }
    `.trim()
    }

    if (this._marketingAttributes && !this._marketingAttributes.fields) {
      throw new SeekQueryEmptyFieldsError()
    }

    let resultingQuery
    if (parameterized) {
      const params = [
        '$type:SeekQueryType!',
        '$dsl:String',
        ...(this._marketingAttributes
          ? [
              '$resolveTags: Boolean',
              '$marketingAttributeTypes: [MarketingAttributeType]',
              '$marketingAttributeClasses: [MarketingAttributeClass]',
            ]
          : []),
      ]
      resultingQuery = {
        query: createQueryString({
          operationName: `${operationName}(${params.join(', ')})`,
          type: '$type',
          dsl: '$dsl',
          ...(this._marketingAttributes
            ? {
                marketingAttributes: {
                  resolveTags: '$resolveTags',
                  types: '$marketingAttributeTypes',
                  classes: '$marketingAttributeClasses',
                  fields: this._marketingAttributes.fields.join(' '),
                },
              }
            : {}),
        }),
        variables: {
          type: this._kind,
          dsl: stringifiedDsl,
          ...(this._marketingAttributes
            ? {
                marketingAttributeTypes: this._marketingAttributes.types,
                marketingAttributeClasses: this._marketingAttributes.classes,
                resolveTags: this._marketingAttributes.resolveTags,
              }
            : {}),
        },
      }
    } else {
      resultingQuery = createQueryString({
        operationName,
        type: this._kind,
        dsl: escapedDsl,
        ...(this._marketingAttributes
          ? {
              marketingAttributes: {
                resolveTags: `${this._marketingAttributes.resolveTags}`,
                types: `[${this._marketingAttributes.types.join(',')}]`,
                classes: `[${this._marketingAttributes.classes.join(',')}]`,
                fields: this._marketingAttributes.fields.join(' '),
              },
            }
          : {}),
      })
    }

    return resultingQuery
  }

  get _applicableFields(): [string] {
    return Array.from(
      this._fields.reduce((set, field) => {
        const abstractMatch = field.match(/\.on:/)

        return abstractMatch
          ? set.add(field.slice(0, abstractMatch.index))
          : set.add(field)
      }, new Set())
    ) as [string]
  }
}

// Null object pattern makes it easier to test this code
class AllQuery implements Query {
  static ALL = {
    all: {},
  }

  toObject() {
    return AllQuery.ALL
  }
}

class SeekQuery implements Query {
  private subQuery: QueryClause

  constructor() {
    this.subQuery = new QueryClause()
  }

  toObject() {
    return this.subQuery.toObject()
  }
}

class QueryClause {
  toObject() {
    return {}
  }
}

export {
  format as formatFieldsToGraphqlQueryFields,
  SeekTypes as seekTypes,
  SeekQuery,
}

export default SeekRequest
